/**
 * AuthZEN PDP Metadata Discovery
 * 
 * Implements PDP discovery per OpenID AuthZEN 1.0 Draft 4 Section 9
 * 
 * Spec Requirements:
 * - Fetch metadata from /.well-known/authzen-configuration
 * - Validate required fields (policy_decision_point, access_evaluation_endpoint)
 * - Use discovered endpoints instead of hardcoded paths
 * - Cache metadata to avoid repeated fetches
 * 
 * Features:
 * - Auto-discovery of PDP endpoints
 * - Capability detection
 * - Graceful fallback to defaults
 * - Metadata caching (1 hour per spec recommendation)
 */

import { AuthZENResponseValidator } from './authz-validator';

export interface PDPMetadata {
	policy_decision_point: string;
	access_evaluation_endpoint: string;
	access_evaluations_endpoint?: string;
	search_subject_endpoint?: string;
	search_resource_endpoint?: string;
	search_action_endpoint?: string;
	capabilities?: string[];
	pdp_name?: string;
	pdp_version?: string;
	authzen_version?: string;
}

export class PDPDiscovery {
	private static metadata: PDPMetadata | null = null;
	private static discovering: Promise<PDPMetadata> | null = null;
	private static metadataTimestamp: number = 0;
	private static readonly METADATA_TTL_MS = 60 * 60 * 1000; // 1 hour per spec Section 11.9
	
	/**
	 * Discover PDP metadata from well-known endpoint
	 * 
	 * Per OpenID AuthZEN Section 9.2, metadata is published at:
	 * /.well-known/authzen-configuration
	 * 
	 * This method:
	 * 1. Fetches metadata from well-known endpoint
	 * 2. Validates required fields
	 * 3. Caches result for 1 hour
	 * 4. Falls back to defaults if discovery fails
	 * 
	 * @param {string} baseUrl - Optional base URL (defaults to current origin)
	 * @returns {PDPMetadata} Discovered and validated PDP metadata
	 * 
	 * @example
	 * ```typescript
	 * const metadata = await PDPDiscovery.discover();
	 * console.log('PDP:', metadata.policy_decision_point);
	 * console.log('Capabilities:', metadata.capabilities);
	 * ```
	 */
	static async discover(baseUrl?: string): Promise<PDPMetadata> {
		// Return cached metadata if still fresh
		if (this.metadata && Date.now() - this.metadataTimestamp < this.METADATA_TTL_MS) {
			return this.metadata;
		}
		
		// Deduplicate concurrent discovery requests
		if (this.discovering) {
			return this.discovering;
		}
		
		this.discovering = (async () => {
			try {
				console.info('[AuthZ] Discovering PDP metadata...');
				
				const url = baseUrl
					? `${baseUrl}/.well-known/authzen-configuration`
					: '/.well-known/authzen-configuration';
				
				const response = await fetch(url, {
					method: 'GET',
					headers: {
						'Accept': 'application/json'
					},
					credentials: 'include',
					// Use cache per Section 11.9
					cache: 'default'
				});
				
				if (!response.ok) {
					throw new Error(
						`PDP discovery failed: HTTP ${response.status} ${response.statusText}`
					);
				}
				
				// Check Content-Type
				const contentType = response.headers.get('content-type');
				if (!contentType?.includes('application/json')) {
					throw new Error(
						`PDP discovery failed: expected application/json, got ${contentType}`
					);
				}
				
				const data = await response.json();
				
				// Validate metadata structure
				const validated = AuthZENResponseValidator.validateMetadataResponse(data);
				
				console.info('[AuthZ] PDP discovered successfully:', {
					pdp: validated.policy_decision_point,
					name: validated.pdp_name || 'Unknown',
					version: validated.pdp_version || 'Unknown',
					authzen_version: validated.authzen_version || 'Unknown',
					capabilities: validated.capabilities?.length || 0
				});
				
				// Cache metadata
				this.metadata = validated;
				this.metadataTimestamp = Date.now();
				
				return validated;
				
			} catch (error) {
				console.error('[AuthZ] PDP discovery failed, using default endpoints:', error);
				
				// Fallback to default endpoints (same origin)
				const origin = baseUrl || window.location.origin;
				const fallback: PDPMetadata = {
					policy_decision_point: origin,
					access_evaluation_endpoint: '/access/v1/evaluation',
					access_evaluations_endpoint: '/access/v1/evaluations',
					search_subject_endpoint: '/access/v1/search/subject',
					search_resource_endpoint: '/access/v1/search/resource',
					search_action_endpoint: '/access/v1/search/action'
				};
				
				// Cache fallback too (retry discovery after TTL)
				this.metadata = fallback;
				this.metadataTimestamp = Date.now();
				
				return fallback;
				
			} finally {
				this.discovering = null;
			}
		})();
		
		return this.discovering;
	}
	
	/**
	 * Get evaluation endpoint URL
	 * 
	 * Uses discovered endpoint or falls back to default
	 * 
	 * @returns {string} Full or relative URL for evaluation endpoint
	 */
	static async getEvaluationEndpoint(): Promise<string> {
		const metadata = await this.discover();
		return metadata.access_evaluation_endpoint;
	}
	
	/**
	 * Get batch evaluation endpoint URL
	 */
	static async getBatchEndpoint(): Promise<string> {
		const metadata = await this.discover();
		return metadata.access_evaluations_endpoint || '/access/v1/evaluations';
	}
	
	/**
	 * Get subject search endpoint URL
	 */
	static async getSubjectSearchEndpoint(): Promise<string> {
		const metadata = await this.discover();
		
		if (!metadata.search_subject_endpoint) {
			throw new Error(
				'PDP does not support subject search (search_subject_endpoint not in metadata)'
			);
		}
		
		return metadata.search_subject_endpoint;
	}
	
	/**
	 * Get resource search endpoint URL
	 */
	static async getResourceSearchEndpoint(): Promise<string> {
		const metadata = await this.discover();
		
		if (!metadata.search_resource_endpoint) {
			throw new Error(
				'PDP does not support resource search (search_resource_endpoint not in metadata)'
			);
		}
		
		return metadata.search_resource_endpoint;
	}
	
	/**
	 * Get action search endpoint URL
	 */
	static async getActionSearchEndpoint(): Promise<string> {
		const metadata = await this.discover();
		
		if (!metadata.search_action_endpoint) {
			throw new Error(
				'PDP does not support action search (search_action_endpoint not in metadata)'
			);
		}
		
		return metadata.search_action_endpoint;
	}
	
	/**
	 * Check if PDP supports a specific capability
	 * 
	 * Per Section 9.1.2, PDPs advertise capabilities as URNs
	 * 
	 * @param {string} capabilityUrn - Capability URN to check
	 * @returns {boolean} True if PDP advertises this capability
	 * 
	 * @example
	 * ```typescript
	 * if (await PDPDiscovery.hasCapability('urn:empowernow:pdp:feature:delegation')) {
	 *   // Show delegation features
	 * }
	 * ```
	 */
	static async hasCapability(capabilityUrn: string): Promise<boolean> {
		const metadata = await this.discover();
		return metadata.capabilities?.includes(capabilityUrn) || false;
	}
	
	/**
	 * Get all PDP capabilities
	 * 
	 * @returns {string[]} Array of capability URNs
	 */
	static async getCapabilities(): Promise<string[]> {
		const metadata = await this.discover();
		return metadata.capabilities || [];
	}
	
	/**
	 * Check if PDP supports search APIs
	 */
	static async supportsSearch(): Promise<{
		subject: boolean;
		resource: boolean;
		action: boolean;
	}> {
		const metadata = await this.discover();
		
		return {
			subject: !!metadata.search_subject_endpoint,
			resource: !!metadata.search_resource_endpoint,
			action: !!metadata.search_action_endpoint
		};
	}
	
	/**
	 * Get PDP information for display
	 */
	static async getPDPInfo(): Promise<{
		name: string;
		version: string;
		authzenVersion: string;
		url: string;
	}> {
		const metadata = await this.discover();
		
		return {
			name: metadata.pdp_name || 'Unknown PDP',
			version: metadata.pdp_version || 'Unknown',
			authzenVersion: metadata.authzen_version || 'Unknown',
			url: metadata.policy_decision_point
		};
	}
	
	/**
	 * Reset cached metadata (useful for testing or after PDP upgrade)
	 * 
	 * Forces next discover() call to fetch fresh metadata
	 */
	static reset(): void {
		this.metadata = null;
		this.metadataTimestamp = 0;
		this.discovering = null;
		
		console.info('[AuthZ] Metadata cache cleared, will re-discover on next call');
	}
	
	/**
	 * Check if metadata has been discovered
	 */
	static isDiscovered(): boolean {
		return this.metadata !== null;
	}
	
	/**
	 * Get cached metadata without triggering discovery
	 * 
	 * Returns null if not yet discovered
	 */
	static getCached(): PDPMetadata | null {
		// Check if cache is still fresh
		if (this.metadata && Date.now() - this.metadataTimestamp < this.METADATA_TTL_MS) {
			return this.metadata;
		}
		
		return null;
	}
}

